import React, { useState, useEffect } from 'react';
import { useTranslation } from '../contexts/LanguageContext';
import '../styles/LobbyTutorial.css';

const TUTORIAL_STORAGE_KEY = 'chess_kingdom_tutorial_completed';

const LobbyTutorial = ({ onComplete }) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  const tutorialSteps = [
    {
      target: 'quick-play-btn',
      title: t('tutorial.quickPlay.title', 'Quick Play'),
      description: t('tutorial.quickPlay.description', 'Jump into a game instantly! Get matched with an AI opponent or practice your skills.'),
      position: 'right',
      icon: '⚡'
    },
    {
      target: 'game-setup-btn',
      title: t('tutorial.gameSetup.title', 'Game Setup'),
      description: t('tutorial.gameSetup.description', 'Customize your game settings, choose time controls, and select chess variants.'),
      position: 'right',
      icon: '♟️'
    },
    {
      target: 'multiplayer-btn',
      title: t('tutorial.multiplayer.title', 'Multiplayer'),
      description: t('tutorial.multiplayer.description', 'Create or join rooms to play with friends. Share room codes for private matches!'),
      position: 'right',
      icon: '👥'
    },
    {
      target: 'tournament-btn',
      title: t('tutorial.tournament.title', 'Tournaments'),
      description: t('tutorial.tournament.description', 'Compete in tournaments! Choose from Single Elimination, Double Elimination, or Swiss formats.'),
      position: 'right',
      icon: '🏆'
    },
    {
      target: 'ranked-btn',
      title: t('tutorial.ranked.title', 'Ranked Matches'),
      description: t('tutorial.ranked.description', 'Test your skills in ranked matches. Climb from Squire to King and earn ELO ratings!'),
      position: 'right',
      icon: '⚔️'
    },
    {
      target: 'skins-btn',
      title: t('tutorial.skins.title', 'Skin Store'),
      description: t('tutorial.skins.description', 'Customize your chess pieces and boards. Free skins available, plus premium options!'),
      position: 'left',
      icon: '🎨'
    },
    {
      target: 'friends-btn',
      title: t('tutorial.friends.title', 'Friends & Chat'),
      description: t('tutorial.friends.description', 'Add friends, chat with them, and invite them to games directly!'),
      position: 'left',
      icon: '💬'
    },
    {
      target: 'settings-btn',
      title: t('tutorial.settings.title', 'Settings'),
      description: t('tutorial.settings.description', 'Adjust game preferences, sound settings, and customize your experience.'),
      position: 'left',
      icon: '⚙️'
    }
  ];

  useEffect(() => {
    // Highlight the current target element
    const targetElement = document.querySelector(`[data-testid="${tutorialSteps[currentStep]?.target}"]`);
    if (targetElement) {
      targetElement.classList.add('tutorial-highlight');
      
      // Scroll element into view if needed
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    return () => {
      // Clean up highlight
      const allHighlighted = document.querySelectorAll('.tutorial-highlight');
      allHighlighted.forEach(el => el.classList.remove('tutorial-highlight'));
    };
  }, [currentStep, tutorialSteps]);

  const handleNext = () => {
    if (currentStep < tutorialSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeTutorial();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    completeTutorial();
  };

  const completeTutorial = () => {
    // Mark tutorial as completed in localStorage
    sessionStorage.setItem(TUTORIAL_STORAGE_KEY, 'true');
    setIsVisible(false);
    if (onComplete) {
      onComplete();
    }
  };

  if (!isVisible) return null;

  const step = tutorialSteps[currentStep];
  const isLastStep = currentStep === tutorialSteps.length - 1;
  const isFirstStep = currentStep === 0;

  return (
    <div className="tutorial-overlay" data-testid="lobby-tutorial">
      {/* Dark overlay */}
      <div className="tutorial-backdrop" />
      
      {/* Tutorial card */}
      <div className={`tutorial-card tutorial-position-${step.position}`} data-testid="tutorial-card">
        {/* Progress indicator */}
        <div className="tutorial-progress">
          {tutorialSteps.map((_, index) => (
            <div 
              key={`step-${index}`}
              className={`progress-dot ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
            />
          ))}
        </div>

        {/* Step content */}
        <div className="tutorial-content">
          <div className="tutorial-icon">{step.icon}</div>
          <h3 className="tutorial-title">{step.title}</h3>
          <p className="tutorial-description">{step.description}</p>
        </div>

        {/* Navigation buttons */}
        <div className="tutorial-actions">
          <button 
            className="tutorial-btn skip-btn"
            onClick={handleSkip}
            data-testid="tutorial-skip"
          >
            {t('tutorial.skip', 'Skip Tutorial')}
          </button>
          
          <div className="tutorial-nav">
            {!isFirstStep && (
              <button 
                className="tutorial-btn prev-btn"
                onClick={handlePrevious}
                data-testid="tutorial-prev"
              >
                {t('tutorial.previous', 'Previous')}
              </button>
            )}
            <button 
              className="tutorial-btn next-btn"
              onClick={handleNext}
              data-testid="tutorial-next"
            >
              {isLastStep ? t('tutorial.finish', 'Get Started!') : t('tutorial.next', 'Next')}
            </button>
          </div>
        </div>

        {/* Step counter */}
        <div className="tutorial-counter">
          {currentStep + 1} / {tutorialSteps.length}
        </div>
      </div>

      {/* Pointer arrow to target */}
      <div className={`tutorial-pointer tutorial-pointer-${step.position}`} />
    </div>
  );
};

// Helper function to check if tutorial should be shown
export const shouldShowTutorial = () => {
  return sessionStorage.getItem(TUTORIAL_STORAGE_KEY) !== 'true';
};

// Helper function to reset tutorial (for testing)
export const resetTutorial = () => {
  sessionStorage.removeItem(TUTORIAL_STORAGE_KEY);
};

export default LobbyTutorial;
